import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getPdvErroLogs } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';

export default function PdvErroLogsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getPdvErroLogs({ qtde: 200 });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível carregar os erros do PDV.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Erros do PDV"
        subtitle="Falhas enviadas pelos caixas (NFC-e, sync, crashes)."
        actions={
          <Button startIcon={<RefreshIcon />} variant="outlined" onClick={load} disabled={loading}>
            Atualizar
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <Paper variant="outlined">
        <ResponsiveTable>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Quando (UTC)</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>PDV</TableCell>
                <TableCell>Mensagem</TableCell>
                <TableCell>Plataforma</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhum erro registrado ainda.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell data-label="Quando">
                      {item.dataHoraUtc
                        ? new Date(item.dataHoraUtc).toLocaleString('pt-BR')
                        : '—'}
                    </TableCell>
                    <TableCell data-label="Origem">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip size="small" label={item.origem || 'pdv'} />
                        <Chip
                          size="small"
                          color={item.nivel === 'error' ? 'error' : 'default'}
                          label={item.nivel || 'error'}
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell data-label="PDV">
                      {item.pdvId || '—'}
                      {item.codigoInstalacao ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {item.codigoInstalacao}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell data-label="Mensagem" sx={{ maxWidth: 480, whiteSpace: 'pre-wrap' }}>
                      <Typography variant="body2">{item.mensagem}</Typography>
                      {item.stackTrace ? (
                        <Typography
                          variant="caption"
                          component="pre"
                          sx={{
                            mt: 0.5,
                            maxHeight: 120,
                            overflow: 'auto',
                            color: 'text.secondary',
                            fontFamily: 'monospace',
                          }}
                        >
                          {item.stackTrace}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell data-label="Plataforma">
                      {item.plataforma || '—'}
                      {item.versaoApp ? ` · ${item.versaoApp}` : ''}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ResponsiveTable>
      </Paper>
    </Box>
  );
}
